/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import i18n, { type TFunction } from "i18next";
import { initReactI18next } from "react-i18next";
import HttpApi from "i18next-http-backend";
import {
  availableLocales,
  LocaleValidator,
  sanitizeBuildNumber,
} from "@centralwebteam/narwhal";
import defaultBuildConfig from "@/config/buildinfo.json";

let currentBuildNumber = sanitizeBuildNumber(
  defaultBuildConfig.buildnumber || "unknown"
);

export function setBuildNumber(buildNumber: string) {
  currentBuildNumber = sanitizeBuildNumber(buildNumber || "unknown");
}

export const i18nextConfig = {
  backend: {
    loadPath: (lngs: readonly string[], namespaces: readonly string[]) =>
      `static/locales/${lngs[0]}/${namespaces[0]}.i18n.json?v=${currentBuildNumber}`,
  },
  cleanCode: true,
  detection: {
    // Use querystring e.g. `lng=fr` to override language
    order: ["querystring", "localStorage", "navigator", "htmlTag"],
  },
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  ns: ["central-web-app", "central-web-app-components"],
  returnEmptyString: false,
  // This means it'll support "de-de" as well as "de", but will make a request for "de-de" first and get a 404. https://github.com/i18next/i18next/issues/1687
  nonExplicitSupportedLngs: false,
  // We are using simple language codes here, e.g. "pt" rather than "pt-BR".
  supportedLngs: availableLocales,
};

i18n.use(HttpApi).use(LocaleValidator).use(initReactI18next);

let resolveI18nReady: (t: TFunction) => void;
const i18nReady = new Promise<TFunction>((resolve) => {
  resolveI18nReady = resolve;
});

export function initI18n() {
  return i18n.init(i18nextConfig).then((t) => {
    resolveI18nReady(t);
    return t;
  });
}

export default i18nReady;
